import {
  DEFAULT_INSPECTION_SCHEDULE,
  nextInspectionRun,
} from "@repo/shared/inspectionSchedule";
/**
 * The inspector's runs, as the pages read them: shapes the server returns,
 * labels, durations, the next weekly run, and the slices the charts draw.
 * Pure, so the arithmetic is pinned by tests.
 *
 * @module components/regulation/inspector/inspectorView
 */
import type { InspectionTrigger } from "@repo/shared/inspectionTriggers";
import { displayChangeComment } from "../finding/changes/changeDiscussion";
import { KIND_LABELS } from "../policy/suggestionsView";

export type { InspectionTrigger };
export type InspectionStatus = "queued" | "running" | "completed" | "failed" | "interrupted";

export type {
  InspectionRun,
  InspectionDetail,
  InspectionItems,
} from "../../../state/inspectionSchemas";
import type {
  InspectionRun,
  InspectionDetail,
  InspectionItems,
} from "../../../state/inspectionSchemas";
export type InspectionFound = InspectionRun["found"];

export const TRIGGER_LABEL: Record<InspectionTrigger, string> = {
  daily: "Daily sweep",
  weekly: "Weekly sweep",
  upload: "New document",
  manual: "Library review",
  finding: "Finding review",
  reassessment: "Re-assessment",
};

export const STATUS_PILL: Record<
  InspectionStatus,
  { readonly label: string; readonly className: string; readonly iconClassName: string }
> = {
  queued: { label: "Queued", className: "bg-muted text-muted-foreground", iconClassName: "text-muted-foreground" },
  running: { label: "Running", className: "bg-primary/10 text-primary", iconClassName: "text-primary" },
  completed: { label: "Completed", className: "bg-success/10 text-success", iconClassName: "text-success" },
  failed: { label: "Failed", className: "bg-destructive/10 text-destructive", iconClassName: "text-destructive" },
  interrupted: { label: "Stopped", className: "bg-warning/12 text-warning", iconClassName: "text-warning" },
};

/** "4m 12s", "1h 03m", "under a second" — a run's length in words a person scans. */
export function formatDuration(startIso: string, endIso: string | null, now = Date.now()): string {
  const start = Date.parse(startIso);
  const end = endIso === null ? now : Date.parse(endIso);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return "—";
  const seconds = Math.max(0, Math.round((end - start) / 1000));
  if (seconds < 1) return "under a second";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${String(seconds % 60).padStart(2, "0")}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
}

/** The next Monday 07:00 UTC after `now` — when the weekly sweep fires. */
/**
 * Whole days, hours and minutes until `at` — for the schedule card's
 * countdown. Never negative: a moment already passed reads as zero.
 */
export function countdownParts(
  at: Date,
  now = Date.now(),
): { readonly days: number; readonly hours: number; readonly minutes: number } {
  const total = Math.max(0, Math.floor((at.getTime() - now) / 60_000));
  return { days: Math.floor(total / 1440), hours: Math.floor((total % 1440) / 60), minutes: total % 60 };
}

/** The default schedule's next run — kept for callers that have no profile yet. */
export function nextWeeklyRun(now = Date.now()): Date {
  return nextInspectionRun(DEFAULT_INSPECTION_SCHEDULE, now) ?? new Date(now);
}

export interface Slice {
  readonly key: string;
  readonly label: string;
  readonly count: number;
}

/** Suggestions by kind, most first — the donut's data. */
export function kindSlices(items: InspectionItems["suggestions"]): Slice[] {
  const counts = new Map<string, number>();
  for (const item of items) counts.set(item.kind, (counts.get(item.kind) ?? 0) + 1);
  return [...counts.entries()]
    .map(([key, count]) => ({
      key,
      label: KIND_LABELS[key as keyof typeof KIND_LABELS] ?? key,
      count,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

const SEVERITY_ORDER = ["critical", "high", "medium", "low", "unrated"] as const;

/** Findings by severity in the order a reader triages, zeros kept so the bars line up. */
export function severitySlices(items: InspectionItems["findings"]): Slice[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    const key = item.severity ?? "unrated";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return SEVERITY_ORDER.map((key) => ({
    key,
    label: key === "unrated" ? "Unrated" : key.charAt(0).toUpperCase() + key.slice(1),
    count: counts.get(key) ?? 0,
  })).filter((slice) => slice.count > 0 || slice.key !== "unrated");
}

/** The assistant's progress lines, in order, without the closing report. */
export function progressSteps(
  detail: InspectionDetail,
): ReadonlyArray<{ readonly text: string; readonly createdAt: string }> {
  const assistant = detail.messages.filter((message) => message.role === "assistant");
  const steps =
    detail.run.status === "completed" &&
    detail.report !== null &&
    assistant.at(-1)?.text === detail.report
      ? assistant.slice(0, -1)
      : assistant;
  return steps.map((message) => ({ text: message.text, createdAt: message.createdAt }));
}

/** Tomorrow 07:00 in the browser's zone — the default for "schedule a run". */
export function defaultScheduleAt(now = Date.now()): Date {
  const at = new Date(now);
  at.setDate(at.getDate() + 1);
  at.setHours(7, 0, 0, 0);
  return at;
}

/** `datetime-local` wants "YYYY-MM-DDTHH:mm" in local time. */
export function toDateTimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

import { inspectionActive } from "../../../state/inspectionSchemas";
export { inspectionActive };
export const inspectionTitle = (run: InspectionRun) =>
  displayChangeComment(run.review?.instruction?.trim() ?? "") ||
  (run.trigger ? TRIGGER_LABEL[run.trigger] : "Library inspection");
/** What kind of run it was, as the filter groups them. */
export type InspectionKind = "finding" | "library" | "sweep" | "upload" | "reassessment";
export const INSPECTION_KINDS: ReadonlyArray<InspectionKind> = [
  "finding",
  "reassessment",
  "library",
  "sweep",
  "upload",
];
export const KIND_LABEL: Record<InspectionKind, string> = {
  finding: "Finding review",
  // Its own filter rather than folded into "library": a reader looking for why
  // a finding changed under them needs to find these runs, and there is one per
  // document version rather than one a person asked for.
  reassessment: "Re-assessment",
  library: "Library review",
  sweep: "Scheduled sweep",
  upload: "New document",
};
export function inspectionKind(run: Pick<InspectionRun, "trigger">): InspectionKind {
  switch (run.trigger) {
    case "finding":
      return "finding";
    case "upload":
      return "upload";
    case "reassessment":
      return "reassessment";
    case "daily":
    case "weekly":
      return "sweep";
    default:
      return "library";
  }
}

export const INSPECTION_STATUSES: ReadonlyArray<InspectionStatus> = [
  "running",
  "queued",
  "completed",
  "failed",
  "interrupted",
];

/** Which kinds and statuses to show; an empty list means any. */
export interface InspectionFilters {
  readonly kinds: ReadonlyArray<InspectionKind>;
  readonly statuses: ReadonlyArray<InspectionStatus>;
}
export const NO_FILTERS: InspectionFilters = { kinds: [], statuses: [] };
export const activeFilterCount = (filters: InspectionFilters) =>
  filters.kinds.length + filters.statuses.length;

const toggled = <T,>(values: ReadonlyArray<T>, value: T, on: boolean): ReadonlyArray<T> =>
  on ? (values.includes(value) ? values : [...values, value]) : values.filter((v) => v !== value);
export const toggleKind = (filters: InspectionFilters, kind: InspectionKind, on: boolean): InspectionFilters => ({
  ...filters,
  kinds: toggled(filters.kinds, kind, on),
});
export const toggleStatus = (
  filters: InspectionFilters,
  status: InspectionStatus,
  on: boolean,
): InspectionFilters => ({ ...filters, statuses: toggled(filters.statuses, status, on) });

export function filterInspections(
  runs: ReadonlyArray<InspectionRun>,
  filters: InspectionFilters,
  query: string,
) {
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  return runs.filter((run) => {
    if (filters.kinds.length > 0 && !filters.kinds.includes(inspectionKind(run))) return false;
    if (filters.statuses.length > 0 && !filters.statuses.includes(run.status)) return false;
    const text =
      `${inspectionTitle(run)} ${run.summary ?? ""} ${run.trigger ?? ""}`.toLocaleLowerCase();
    return words.every((word) => text.includes(word));
  });
}

export type InspectionSort = "newest" | "oldest" | "longest" | "status";
export const INSPECTION_SORTS: ReadonlyArray<InspectionSort> = ["newest", "oldest", "longest", "status"];
export const SORT_LABEL: Record<InspectionSort, string> = {
  newest: "Newest first",
  oldest: "Oldest first",
  longest: "Longest first",
  status: "By status",
};
const STATUS_ORDER: Record<InspectionStatus, number> = {
  running: 0,
  queued: 1,
  failed: 2,
  interrupted: 3,
  completed: 4,
};
const runLength = (run: InspectionRun, now: number) => {
  const start = Date.parse(run.startedAt);
  const end = run.completedAt ? Date.parse(run.completedAt) : inspectionActive(run) ? now : start;
  return Number.isFinite(start) && Number.isFinite(end) ? Math.max(0, end - start) : 0;
};
/**
 * Newest first keeps runs still in progress at the top, so what is happening
 * now is never buried; the other orders are exactly what they say.
 */
export function sortInspections(
  runs: ReadonlyArray<InspectionRun>,
  sort: InspectionSort,
  now = Date.now(),
): ReadonlyArray<InspectionRun> {
  const byNewest = (a: InspectionRun, b: InspectionRun) => Date.parse(b.startedAt) - Date.parse(a.startedAt);
  const sorted = [...runs];
  switch (sort) {
    case "newest":
      return sorted.sort(
        (a, b) => Number(inspectionActive(b)) - Number(inspectionActive(a)) || byNewest(a, b),
      );
    case "oldest":
      return sorted.sort((a, b) => -byNewest(a, b));
    case "longest":
      return sorted.sort((a, b) => runLength(b, now) - runLength(a, now) || byNewest(a, b));
    case "status":
      return sorted.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || byNewest(a, b));
  }
}
export function inspectionStatusCopy(run: InspectionRun) {
  switch (run.status) {
    case "queued":
      return "Waiting to start. This page updates automatically.";
    case "running":
      return "Inspector is reviewing the request. Any proposed changes still need review.";
    case "completed":
      return run.review
        ? "Review finished. Check the response and any proposed changes."
        : "Inspection finished. Review the report and items below.";
    case "failed":
      return run.error?.trim() || "Inspector could not finish this run. Try again, or contact support if it keeps failing.";
    case "interrupted":
      return "This run stopped before finishing. Any proposed changes still need review.";
  }
}

/**
 * One page of a list for the runs table's footer: the page is clamped into
 * range (a filter that shrinks the list never leaves you on an empty page),
 * `from`/`to` are 1-based for "Showing 11–20 of 23".
 */
export function paginate<T>(
  items: ReadonlyArray<T>,
  page: number,
  pageSize: number,
): {
  readonly items: ReadonlyArray<T>;
  readonly page: number;
  readonly pages: number;
  readonly from: number;
  readonly to: number;
} {
  const size = Math.max(1, Math.floor(pageSize));
  const pages = Math.max(1, Math.ceil(items.length / size));
  const current = Math.min(Math.max(1, Math.floor(page)), pages);
  const start = (current - 1) * size;
  const slice = items.slice(start, start + size);
  return {
    items: slice,
    page: current,
    pages,
    from: items.length === 0 ? 0 : start + 1,
    to: start + slice.length,
  };
}
